<template>
  <div>
    <nav class="desktop-menu hidden items-center navigation-placeholder bg-white shadow-md">
      <AtomLink :url="{ name: 'index' }" :title="t_('logo-label')" :aria-label="t_('logo-label')" class="ml-10" @click="gtmNavigationClick('Home')">
        <AtomBrandConcertgebouwLogo :aria-hidden="true" role="presentation" class="text-black w-24 mr-16" />
      </AtomLink>

      <div class="flex flex-row h-full w-full relative">
        <ol id="main-navigation" class="flex flex-row" role="menu" :aria-label="t_('menu')">
          <li
            v-for="item in menu"
            :key="item.id"
            role="menuitem"
            class="c-main-menu-item flex text-base mr-8 2xl:text-lg 2xl:mr-6 border-b-4 hover:border-app-primary-1-red--fire-brick border-solid items-center cursor-pointer transition-colors duration-300 ignore-close"
            :class="{ 'border-app-primary-1-red--fire-brick': item.isActive, 'border-transparent': !item.isActive, 'c-main-menu-item__open': item.isOpen }"
            @mouseenter="item.children.length ? navigationStore.setOpenItem(item.id) : undefined"
            @mouseleave="item.children.length ? navigationStore.closeAll() : undefined"
          >
            <!-- If item doesn't have children, it is a direct link directly to a url -->
            <AtomLink
              :url="!item.children.length ? item.url : undefined"
              fallback-component="button"
              class="-mb-2 hover:text-app-primary-1-red--fire-brick flex items-center"
              :aria-expanded="item.children.length ? (item.isOpen ? 'true' : 'false') : undefined"
              @click="!item.children.length ? gtmNavigationClick(item.title) : undefined"
              @keydown.enter="item.children.length ? navigationStore.toggleOpen(item.id) : undefined"
              @focusin="navigationStore.closeAll([item.id])"
            >
              <span>{{ item.title }}</span>
              <AtomIconChevronDown
                v-if="item.children.length"
                class="c-main-menu-item__chevron w-3 h-3 inline-block ml-2 text-app-primary-1-red--fire-brick transition duration-300 ease-in-out transform"
              />
            </AtomLink>

            <div
              v-if="item.children.length"
              class="invisible pointer-events-none transition duration-300 ease-in-out transform opacity-0 absolute bg-white rounded-br-2xl rounded-bl-2xl shadow-inner shadow-md left-0 overflow-hidden mega-menu"
            >
              <ol class="grid gap-x-16 grid-cols-2" aria-label="Sub navigation">
                <li v-for="child in item.children" :key="child.id" role="menuitem" class="h-full">
                  <MoleculeCardMenuCategory :child="child" @click="gtmNavigationClick(child.title)" />
                </li>
              </ol>
            </div>
          </li>
        </ol>
        <div class="flex flex-row ml-auto mr-10 items-center">
          <ol id="service-navigation" class="flex flex-row" aria-label="service-menu">
            <li class="mr-6">
              <AtomLink v-if="favoritesEnabled" :url="get(globalStore.favoritePage, 'url')">
                <span class="v-service-menu-hover text-xs mr-2">
                  {{ t_('favorites') }}<template v-if="favoritesEventUris"> ({{ favoritesEventUris }})</template></span
                >
                <AtomIconBookmark class="w-5 h-5 inline-block" />
              </AtomLink>
              <button v-else @click="favoritesOpenOverlay()">
                <span class="v-service-menu-hover text-xs mr-2"> {{ t_('favorites') }}</span>
                <AtomIconBookmark class="w-5 h-5 inline-block" />
              </button>
            </li>
            <li class="mr-6">
              <button @click="toggleSearching">
                <span class="v-service-menu-hover text-xs mr-2"> {{ t_('search') }}</span>
                <AtomIconAppSearch class="w-5 h-5 inline-block" />
              </button>
            </li>
            <li v-if="tixOrder" class="mr-6">
              <AtomLink :url="tixOrderUrl" target="_self" class="relative cursor-pointer">
                <span class="v-service-menu-hover text-xs mr-2">{{ t_('basket') }}</span>
                <AtomIconBasket class="w-5 h-5 inline-block" />
                <AtomBasketNumber class="absolute right-0 top-0 -mt-4 -mr-3">
                  <span>{{ tixOrderSize }}</span>
                </AtomBasketNumber>
              </AtomLink>
            </li>
            <li class="mr-6">
              <AtomLink :url="tixUser ? tixProfileUrl : tixLoginUrl" target="_self" rel="nofollow">
                <span class="v-service-menu-hover text-xs mr-2"> {{ t_(tixUser ? 'profile' : 'login') }}</span>
                <span aria-hidden="true" class="relative inline-block">
                  <AtomIconAppPerson class="w-5 h-5 inline-block" />
                  <MoleculeABTestWrapper experiment-code="cv11" default-variant="A">
                    <template #A>
                      <AtomIconCircledCheckFilled v-if="tixUser" class="text-app-secondary-8-blue--medium-aquamarine absolute w-3 h-3 inline-block bottom-[-3px] right-[-3px]" />
                    </template>
                    <template #B>
                      <AtomIconCircledCheckFilled v-if="tixUser" class="text-[#1E9250] absolute w-3 h-3 inline-block bottom-[-3px] right-[-3px]" />
                      <AtomIconCircledCrossFilled v-if="!tixUser" class="text-[#CE072F] absolute w-3 h-3 inline-block bottom-[-3px] right-[-3px]" />
                    </template>
                  </MoleculeABTestWrapper>
                </span>
              </AtomLink>
            </li>
            <li class="flex items-center">
              <AtomLink
                v-for="{ language, url, isCurrent } in pageStore.languageSwitches"
                :key="language"
                :url="url"
                class="mr-2 uppercase text-sm font-semibold"
                :class="{ 'v-service-menu-hover text-gray-500': !isCurrent }"
              >
                {{ language }}
              </AtomLink>
            </li>
          </ol>
        </div>

        <transition-group name="fade">
          <div
            v-if="searching"
            key="menu_group_1"
            class="absolute px-16 py-6 bg-app-primary-grey--black-squeeze rounded-br-2xl rounded-bl-2xl shadow-inner shadow-md right-0 z-10 service-menu"
          >
            <MoleculeInputWithIcon ref="searchInputDesktop" :placeholder="t_('search-placeholder')" icon="search" @submit="submitSearch" />
          </div>

          <div v-if="searching" key="menu_group_2" class="absolute px-3 py-3 bg-white z-50 rounded-bl-2xl right-0 cursor-pointer searching-close" @click="toggleSearching">
            <AtomIconX class="w-2 h-2" />
          </div>
        </transition-group>
      </div>
    </nav>

    <transition name="fade">
      <div v-if="searching" class="fixed w-screen h-full bg-black opacity-75 z-0" @click="toggleSearching"></div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import get from 'lodash/get'
import { explodeUrl } from '@/assets/link'

const router = useRouter()
const navigationStore = useNavigationStore()
const globalStore = useGlobalStore()
const favoritesStore = useFavoritesStore()
const pageStore = usePageStore()
const { tixOrder, tixOrderUrl, tixOrderSize, tixProfileUrl, tixUser, tixLoginUrl } = setupTix()
const { t_ } = setupTranslationPrefixer('navigation', null)
const gtmNavigationClick = setupGtmNavigationClick()

const searching = ref(false)
const searchInputDesktop = ref<HTMLInputElement | null>(null)

const menu = computed(() => navigationStore.menu)
const favoritesEventUris = computed(() => favoritesStore.eventUris.filter((item: string) => item).length)
const favoritesEnabled = computed(() => favoritesStore.enabled)

const toggleSearching = () => {
  searching.value = !searching.value

  setTimeout(() => {
    if (searchInputDesktop.value) {
      searchInputDesktop.value.focus()
    }
  }, 100)

  if (searching.value) {
    document.body.classList.add('overflow-hidden')
    return
  }

  document.body.classList.remove('overflow-hidden')
}

const submitSearch = (value: string) => {
  toggleSearching()
  searching.value = false

  const path = explodeUrl(globalStore.search?.url || '').path
  router.push({ path, query: { keyword: value } })
}

const favoritesOpenOverlay = () => {
  favoritesStore.openOverlayAndStoreId()
}
</script>

<style lang="scss" scoped>
.desktop-menu {
  // On Windows 10, the default display scaling is set to 150%, which caused the navbar links to wrap onto two lines at the xl breakpoint. To fix this issue, the breakpoint was adjusted accordingly.
  @media (min-width: 1290px) {
    display: flex;
  }
}

.c-main-menu-item__open {
  .c-main-menu-item__chevron {
    transform: rotate(180deg);
  }

  .mega-menu {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.navigation-placeholder {
  height: 68px;
}

.mega-menu {
  top: 68px;

  .mega-menu-item {
    width: 340px;
  }
}

.service-menu {
  top: 68px;

  @screen md {
    width: 577px;
  }
}

.v-service-menu-hover {
  text-decoration: underline 2px rgba(0, 0, 0, 0);
  text-underline-offset: 4px;

  &:hover,
  &:focus {
    text-decoration-color: inherit;
    transition: text-decoration-color 300ms;
  }
}

.searching-close {
  top: 68px;
}

@media (min-width: 1290px) and (max-width: 1340px) {
  .c-main-menu-item {
    @apply mr-[1.4rem];
  }
}
</style>
